import { useCallback, useEffect, useState } from "react";
import { Save, CheckCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Loading, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const ESTADOS = [
  { v: "presente", l: "Presente", c: "border-emerald-500 bg-emerald-500 text-white" },
  { v: "tardanza", l: "Tarde", c: "border-amber-500 bg-amber-500 text-white" },
  { v: "ausente", l: "Ausente", c: "border-rose-500 bg-rose-500 text-white" },
  { v: "excusa", l: "Excusa", c: "border-sky-500 bg-sky-500 text-white" },
];

export const PanelAsistencia = ({ materia }) => {
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [filas, setFilas] = useState(null);
  const [busy, setBusy] = useState(false);

  const cargar = useCallback(async (f) => {
    setFilas(null);
    try {
      const { data } = await api.get(`/docente/materias/${materia.id}/asistencia`, { params: { fecha: f } });
      setFilas(data.estudiantes);
    } catch (e) {
      toast.error(apiError(e));
      setFilas([]);
    }
  }, [materia.id]);

  useEffect(() => { cargar(fecha); }, [cargar, fecha]);

  const marcar = (idx, estado) => {
    const copia = [...filas];
    copia[idx] = { ...copia[idx], estado };
    setFilas(copia);
  };

  const todosPresentes = () => setFilas(filas.map((f) => ({ ...f, estado: f.estado || "presente" })));

  const guardar = async () => {
    const items = filas.filter((f) => f.estado).map((f) => ({
      estudiante_id: f.id, estado: f.estado, nota: f.nota_asistencia || null,
    }));
    if (items.length === 0) return toast.error("Marque la asistencia de al menos un estudiante");
    setBusy(true);
    try {
      const { data } = await api.post("/docente/asistencia", { materia_id: materia.id, fecha, items });
      toast.success(`Asistencia de ${data.guardadas} estudiante(s) guardada`);
      cargar(fecha);
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  return (
    <div data-testid="panel-asistencia">
      <div className="mt-6 flex flex-wrap items-end gap-4">
        <div className="space-y-2">
          <Label>Fecha de la clase</Label>
          <Input type="date" value={fecha} className="w-44" data-testid="asistencia-fecha"
                 onChange={(e) => setFecha(e.target.value)} />
        </div>
        <Button variant="outline" size="sm" className="rounded-full" onClick={todosPresentes}
                disabled={!filas?.length} data-testid="todos-presentes">
          <CheckCheck className="mr-2 h-3 w-3" /> Marcar todos presentes
        </Button>
      </div>

      {!filas ? <Loading label="Cargando estudiantes…" /> : filas.length === 0 ? (
        <Empty testId="sin-estudiantes-asistencia">No hay estudiantes activos en {materia.grado}.</Empty>
      ) : (
        <>
          <div className="mt-6 space-y-3" data-testid="lista-asistencia">
            {filas.map((f, idx) => (
              <div key={f.id} className="flex flex-wrap items-center gap-3 rounded-md border border-border/60 p-4">
                <div className="min-w-[160px] flex-1">
                  <p className="text-sm font-medium">{f.nombre}</p>
                  <p className="font-mono text-xs text-muted-foreground">{f.matricula}</p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {ESTADOS.map((e) => (
                    <button key={e.v} onClick={() => marcar(idx, e.v)}
                            data-testid={`estado-${f.matricula}-${e.v}`}
                            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                              f.estado === e.v ? e.c
                                : "border-border text-muted-foreground hover:bg-muted"}`}>
                      {e.l}
                    </button>
                  ))}
                </div>
                <Input value={f.nota_asistencia ?? ""} placeholder="Nota (opcional)" className="w-full sm:w-44"
                       data-testid={`asistencia-obs-${f.matricula}`}
                       onChange={(e) => {
                         const copia = [...filas];
                         copia[idx] = { ...f, nota_asistencia: e.target.value };
                         setFilas(copia);
                       }} />
                {f.porcentaje != null && (
                  <Badge variant={f.porcentaje >= 85 ? "secondary" : "destructive"} className="font-mono text-[10px]"
                         data-testid={`porcentaje-${f.matricula}`}>
                    {f.porcentaje}% · {f.ausencias} aus.
                  </Badge>
                )}
              </div>
            ))}
          </div>
          <Button className="mt-6 rounded-full" onClick={guardar} disabled={busy} data-testid="guardar-asistencia">
            <Save className="mr-2 h-4 w-4" /> {busy ? "Guardando…" : "Guardar asistencia"}
          </Button>
        </>
      )}
    </div>
  );
};
